import { db } from '@/services/storage/db';

// ADR-070: keep-set of tables never cleared on an account switch. `syncConfig`
// holds the owning-user marker and the sync watermark, both of which this
// module mutates individually rather than clearing wholesale.
const KEEP_TABLE_NAMES = new Set(['syncConfig']);

/**
 * Detects and resolves an account switch on this device (ADR-070).
 *
 * Compares the persisted `owningUserId` marker (in `db.syncConfig`) against the
 * candidate user id. If they differ — or no marker exists yet and the local
 * `users` table already holds a different account's rows (D3-bootstrap) — every
 * user-data table is cleared, the sync watermark is reset, and the marker is
 * rewritten to the candidate user, all inside a single atomic transaction.
 *
 * Idempotent and order-independent: once the marker matches `candidateUserId`,
 * repeat calls are a no-op (D5 idempotency contract). Must be awaited by every
 * auth-store call site BEFORE `status` flips to `'authenticated'`, so the reset
 * always completes ahead of `initSync` (D3 ordering guarantee).
 */
export async function resolveAccountSwitch(candidateUserId: string): Promise<void> {
  const marker = await db.syncConfig.get('owningUserId');

  if (marker && marker.value === candidateUserId) {
    // Already owned by this user — safe no-op (D5 contract guarantee 1).
    return;
  }

  if (!marker) {
    // D3-bootstrap: no marker installed yet (pre-existing install, or first-ever
    // login on this device). Probe the FULL users table — not getCurrentUser(),
    // which only returns the first row — to decide whether foreign-user rows
    // are present.
    const rows = await db.users.toArray();
    const hasForeignRows = rows.some((row) => row.id !== candidateUserId);
    if (!hasForeignRows) {
      // Empty DB, or all local rows already belong to this user — preserve
      // existing local data (ADR-070 Consequences: no-wipe single-account
      // upgrade path). Write the marker only.
      await db.syncConfig.put({ key: 'owningUserId', value: candidateUserId });
      return;
    }
  }

  // Marker exists and differs, or marker absent with foreign-user rows present:
  // wipe every user-data table, reset the watermark, and rewrite the marker —
  // all inside one atomic transaction so a mid-operation failure rolls back
  // completely and never leaves a wiped-but-unmarked state (D2/D3).
  const clearTables = db.tables.filter((table) => !KEEP_TABLE_NAMES.has(table.name));

  await db.transaction('rw', [...clearTables, db.syncConfig], async () => {
    await Promise.all(clearTables.map((table) => table.clear()));
    await db.syncConfig.delete('lastSyncedAt');
    // Marker write is the LAST write in the transaction (D3 ordering guarantee).
    await db.syncConfig.put({ key: 'owningUserId', value: candidateUserId });
  });
}
